import { useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import { useAuthStore } from '@/stores/authStore';
import {
  commentBodySchema,
  getProposalComments,
  getProposalPresentation,
  postProposalComment,
  proposalRevision,
} from '@/api/adapter/proposalReviews';

export function useProposalPresentation(project: string, proposal?: string) {
  const principal = useAuthStore((s) => s.session?.principalId);
  return useQuery({
    queryKey: ['proposal-presentation', principal, project, proposal],
    enabled: !!principal && !!proposal,
    retry: false,
    queryFn: async ({ signal }) => {
      const result = await getProposalPresentation(project, proposal!, signal);
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload proposal details.');
      return result;
    },
  });
}
export function useProposalComments(project: string, proposal: string) {
  const principal = useAuthStore((s) => s.session?.principalId);
  return useInfiniteQuery({
    queryKey: ['proposal-comments', principal, project, proposal],
    enabled: !!principal,
    retry: false,
    initialPageParam: 0,
    queryFn: async ({ pageParam, signal }) => {
      const result = await getProposalComments(
        project,
        proposal,
        pageParam,
        signal
      );
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload responses.');
      return result;
    },
    getNextPageParam: (last) => last.nextAfter ?? undefined,
  });
}
const pendingSchema = commentBodySchema
  .omit({ expectedProposalRevision: true })
  .extend({
    expectedProposalRevision: z.number().int().nonnegative().safe().nullable(),
  });
export function useProposalCommentWriter(project: string, proposal: string) {
  const principal = useAuthStore((s) => s.session?.principalId);
  const cache = useQueryClient();
  const responseKey = (id: string) =>
    'proposal-comment:' + principal + ':' + project + ':' + id;
  const key = responseKey(proposal);
  const targetProposal = useRef(proposal);
  const [saved] = useState(() => {
    try {
      const value = sessionStorage.getItem(key);
      return {
        pending: value ? pendingSchema.parse(JSON.parse(value)) : null,
        error: '',
      };
    } catch {
      return {
        pending: null,
        error:
          'The saved response is invalid or inaccessible. Discard the local response to write a new one; no new response will be sent automatically.',
      };
    }
  });
  const [pending, setPending] = useState(saved.pending);
  const [blocked, setBlocked] = useState(!!saved.error);
  const [error, setError] = useState(saved.error);
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  function retain(value: z.infer<typeof pendingSchema> | null) {
    setPending(value);
    if (value)
      sessionStorage.setItem(
        responseKey(targetProposal.current),
        JSON.stringify(value)
      );
    else sessionStorage.removeItem(responseKey(targetProposal.current));
  }
  async function send(
    message?: string,
    observedRevision?: number,
    createdProposal?: string
  ) {
    if (!proposal && createdProposal) targetProposal.current = createdProposal;
    const target = targetProposal.current;
    if (running.current || blocked) return false;
    running.current = true;
    setBusy(true);
    setError('');
    try {
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload before sending.');
      let body = pending;
      if (!body) {
        body = pendingSchema.parse({
          message,
          requestId: '0x' + crypto.randomUUID().replace(/-/g, ''),
          expectedProposalRevision: observedRevision ?? null,
        });
        retain(body);
      }
      if (body.expectedProposalRevision === null) {
        body = {
          ...body,
          expectedProposalRevision: await proposalRevision(project, target),
        };
        retain(body);
      }
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error('Your account changed. Reload before sending.');
      await postProposalComment(project, target, commentBodySchema.parse(body));
      if (useAuthStore.getState().session?.principalId !== principal)
        throw new Error(
          'Your account changed. Reload responses before retrying.'
        );
      retain(null);
      await cache.invalidateQueries({
        queryKey: ['proposal-comments', principal, project, target],
      });
      return true;
    } catch (reason) {
      setError((reason as Error).message);
      return false;
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  function discard() {
    if (
      running.current ||
      useAuthStore.getState().session?.principalId !== principal
    )
      return;
    try {
      sessionStorage.removeItem(responseKey(targetProposal.current));
      setPending(null);
      setBlocked(false);
      setError('');
    } catch {
      setError(
        'The local response could not be discarded. Keep this page open and retry.'
      );
    }
  }
  return { send, discard, pending, error, busy, blocked };
}
